import React from "react";
import { Bug, Droplets, House, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const pestProfiles = [{
  id: "ants",
  title: "Invasive Ants",
  scientific: "Formicidae",
  description: "Thriving in the seasonal humidity of Southwest Florida, invasive ant species quickly establish massive colonies in residential areas from Riverside to deep within Punta Gorda. Their complex networks require targeted baiting rather than simple surface sprays.",
  icon: Bug,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_ants_florida.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_ants_florida.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_ants_florida.png-wvc-sizes",
    alt: "Close up of invasive ants common in Port Charlotte"
  },
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  id: "palmetto-bugs",
  title: "Palmetto Bugs",
  scientific: "Eurycotis floridana",
  description: "Drawn to the moisture near Charlotte Harbor, these resilient roaches seek shelter indoors during heavy summer rains. Our industrial-grade perimeter defense creates an impenetrable barrier around your home's foundation.",
  icon: ShieldAlert,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_palmetto_bug.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_palmetto_bug.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_palmetto_bug.png-wvc-sizes",
    alt: "Palmetto bug resting on a surface"
  },
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  id: "mosquitoes",
  title: "Mosquitoes",
  scientific: "Culicidae",
  description: "The proximity to the Peace River and numerous local canals makes neighborhoods like Murdock prime breeding grounds year-round. We target both adult populations and standing-water breeding sites to break the lifecycle.",
  icon: Droplets,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_bite.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_bite.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_mosquito_bite.png-wvc-sizes",
    alt: "Mosquito macro photography"
  },
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  id: "termites",
  title: "Termites",
  scientific: "Isoptera",
  description: "Subterranean and drywood termites pose a silent, constant threat to wooden structures across Port Charlotte. Thriving in our warm, humid climate, they require aggressive, specialized treatments to eradicate completely before structural damage occurs.",
  icon: House,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_termite_worker.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_termite_worker.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_termite_worker.png-wvc-sizes",
    alt: "Termite damage on wooden structure"
  },
  colSpan: "col-span-12 md:col-span-6 lg:col-span-6"
}, {
  id: "spiders",
  title: "Spiders",
  scientific: "Araneae",
  description: "From harmless house spiders to venomous varieties seeking refuge from the intense Florida heat, our local environment supports a diverse arachnid population. We focus on eliminating their food sources and sealing entry points.",
  icon: Bug,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_wolf_spider.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_wolf_spider.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/macro_wolf_spider.png-wvc-sizes",
    alt: "Local spider species in web"
  },
  colSpan: "col-span-12 md:col-span-6 lg:col-span-6"
}];
export default function LocalPestProfiles() {
  return <section id="localpestprofiles" className="bg-background py-20 md:py-32 border-y-8 border-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-16 max-w-3xl">
          <Badge variant="default" className="mb-6 text-sm px-4 py-1 uppercase tracking-widest font-default">
            Local Threat Analysis
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-foreground mb-6 uppercase leading-tight">
            Targeted Defense For <span className="text-primary">Charlotte County</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-serif leading-relaxed">
            Our pest control strategies are engineered specifically for the unique environmental challenges of Punta Gorda, Port Charlotte, and surrounding coastal communities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {pestProfiles.map((pest, i) => {
          const Icon = pest.icon;
          return <div key={pest.id} data-index={i} className={pest.colSpan}>
                <Card className="h-full border-2 border-border bg-card hover:border-primary transition-colors duration-300 group flex flex-col">
                  <div className="relative border-b-2 border-border">
                    <AspectRatio ratio={16 / 9}>
                      <img src={pest.image.src} data-wvc-srcset={pest.image.srcset} data-wvc-sizes={pest.image.sizes} alt={pest.image.alt} className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                    </AspectRatio>
                    <div className="absolute top-4 right-4 bg-primary p-3 border-2 border-border">
                      <Icon className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                  
                  <CardHeader className="pb-4 pt-6">
                    <div className="flex justify-between items-baseline mb-2">
                      <CardTitle className="text-2xl md:text-3xl font-default uppercase tracking-wide text-foreground">
                        {pest.title}
                      </CardTitle>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                      Class: {pest.scientific}
                    </p>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    <p className="text-base md:text-lg text-card-foreground font-serif leading-relaxed">
                      {pest.description}
                    </p>
                  </CardContent>
                  
                  <div className="h-2 w-full bg-border group-hover:bg-primary transition-colors duration-300 mt-auto" />
                </Card>
              </div>;
        })}
        </div>

      </div>
    </section>;
}